import {Fragment, useCallback, useMemo} from "react";
import {typedMemo} from "../../../../utils/typescript";
import {TableImplProps} from ".";
import {ItemsOrGroups, TableConfig} from "../../../../hooks/table";
import {BaseTableBody, BaseTableHead, BaseTableRoot, BaseTableRow} from "../base";

export const SimpleTableImpl = typedMemo(
  <C extends TableConfig>({
    config,
    renderRowContent,
    renderHeaderContent,
    shouldRenderGroupHeaderRow,
    renderGroupHeaderContent,
    itemsOrGroups,
    rootProps,
    getItemRowProps,
    minWidth,
  }: TableImplProps<C>) => {
    const fixedHeader = useMemo(() => renderHeaderContent(), [renderHeaderContent]);

    const renderItemsOrGroups = useCallback(
      (depth: number, itemsOrGroups: ItemsOrGroups<C>) => {
        if (itemsOrGroups.type === "items") {
          return itemsOrGroups.items.map(item => (
            <BaseTableRow key={config.extractUniqueKey(item)} {...getItemRowProps(item)}>
              {renderRowContent(item)}
            </BaseTableRow>
          ));
        }

        return itemsOrGroups.groups.map(group => (
          <Fragment key={group.key}>
            {shouldRenderGroupHeaderRow(group, depth) && (
              <BaseTableRow>{renderGroupHeaderContent(group, depth)}</BaseTableRow>
            )}
            {renderItemsOrGroups(depth + 1, group.itemsOrGroups)}
          </Fragment>
        ));
      },
      [config, getItemRowProps, renderGroupHeaderContent, renderRowContent, shouldRenderGroupHeaderRow],
    );

    const rows = useMemo(() => {
      return renderItemsOrGroups(0, itemsOrGroups);
    }, [itemsOrGroups, renderItemsOrGroups]);

    return (
      <BaseTableRoot config={config} {...rootProps} {...(minWidth ? {minWidth} : {})}>
        <BaseTableHead>{fixedHeader}</BaseTableHead>
        <BaseTableBody>{rows}</BaseTableBody>
      </BaseTableRoot>
    );
  },
);
